import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getGSTReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportGSTReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';

const B2B_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'B2B (Has GSTN)', value: 'B2B' },
    { label: 'B2C (No GSTN)', value: 'B2C' },
];

const COLUMNS = [
    { header: 'Flat No.', key: 'flat_no' },
    { header: 'Member Name', key: 'member_name' },
    { header: 'GSTN', key: 'gstn' },
    { header: 'Bill Type', key: 'bill_type' },
    { header: 'Voucher Type', key: 'voucher_type' },
    { header: 'Billing Period', key: 'billing_period' },
    { header: 'Bill Date', key: 'bill_date', align: 'center', isDate: true },
    { header: 'Billing Month', key: 'billing_month' },
    { header: 'Bill No.', key: 'bill_no' },
    { header: 'Taxable', key: 'taxable', align: 'right', isCurrency: true },
    { header: 'Non-Taxable', key: 'non_taxable', align: 'right', isCurrency: true },
    { header: 'Exempted', key: 'exempted', align: 'right', isCurrency: true },
    { header: 'CGST', key: 'cgst', align: 'right', isCurrency: true },
    { header: 'SGST', key: 'sgst', align: 'right', isCurrency: true },
    { header: 'IGST', key: 'igst', align: 'right', isCurrency: true },
    { header: 'Round Off', key: 'round_off', align: 'right', isCurrency: true },
    { header: 'Bill Total', key: 'bill_total', align: 'right', isCurrency: true },
    { header: 'GST Total', key: 'gst_total', align: 'right', isCurrency: true },
    { header: 'GST Verify', key: 'gst_verification', align: 'right', isCurrency: true },
    { header: 'GST Diff', key: 'gst_difference', align: 'right', isCurrency: true },
];

const SUMMARY_MAP = {
    taxable: 'total_taxable',
    non_taxable: 'total_non_taxable',
    exempted: 'total_exempted',
    cgst: 'total_cgst',
    sgst: 'total_sgst',
    igst: 'total_igst',
    round_off: 'total_round_off',
    bill_total: 'total_bill',
    gst_total: 'total_gst',
    gst_verification: 'total_gst',
    gst_difference: 'total_gst_difference',
};

const GSTReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { gstReportData, gstReportSummary, gstReportPagination, gstReportLoading } = useSelector(state => state.InvoiceReportReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        invoice_type_id: '', wing_id: '', flat_id: '',
        from_date: '', to_date: '', b2b_b2c: 'ALL', search: '',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filters.wing_id) dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
    }, [dispatch, filters.wing_id]);

    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.from_date) params.from_date = filters.from_date;
        if (filters.to_date) params.to_date = filters.to_date;
        if (filters.b2b_b2c !== 'ALL') params.b2b_b2c = filters.b2b_b2c;
        if (filters.search) params.search = filters.search;
        dispatch(getGSTReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;
            if (filters.b2b_b2c !== 'ALL') params.b2b_b2c = filters.b2b_b2c;
            if (filters.search) params.search = filters.search;
            const response = await exportGSTReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'gst_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({ invoice_type_id: '', wing_id: '', flat_id: '', from_date: '', to_date: '', b2b_b2c: 'ALL', search: '' });
        setPage(1);
    };

    const invoices = gstReportData || [];
    const summary = gstReportSummary || {};
    const pagination = gstReportPagination || {};
    const wings = wingsDropdown || [];
    const flats = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];
    const invoiceTypes = invoiceTypesData || [];

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getCellValue = (inv, col) => {
        if (col.isDate) return formatDate(inv[col.key]);
        if (col.isCurrency) return formatCurrency(inv[col.key]);
        return inv[col.key] || '-';
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">GST Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Tax breakdown for GST filing (GSTR-1)</p>
                </div>
                <button onClick={handleExport} disabled={exporting || gstReportLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50">
                    {exporting ? <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" /> : <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />}
                    {exporting ? 'Exporting...' : 'Export Excel'}
                </button>
            </div>

            {/* Summary Cards */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Invoices</div>
                    <div className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mt-1">{summary.total_invoices || 0}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Taxable</div>
                    <div className="tw-text-xl tw-font-bold tw-text-blue-700 tw-mt-1">{formatCurrency(summary.total_taxable)}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total GST</div>
                    <div className="tw-text-xl tw-font-bold tw-text-emerald-700 tw-mt-1">{formatCurrency(summary.total_gst)}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">GST Difference</div>
                    <div className={`tw-text-xl tw-font-bold tw-mt-1 ${(summary.total_gst_difference || 0) !== 0 ? 'tw-text-red-600' : 'tw-text-emerald-700'}`}>
                        {formatCurrency(summary.total_gst_difference)}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 lg:tw-grid-cols-7 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Invoice Type</label>
                        <select value={filters.invoice_type_id} onChange={(e) => handleFilterChange('invoice_type_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Types</option>
                            {invoiceTypes.map(t => <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select value={filters.wing_id} onChange={(e) => handleFilterChange('wing_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Wings</option>
                            {wings.map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select value={filters.flat_id} onChange={(e) => handleFilterChange('flat_id', e.target.value)} disabled={!filters.wing_id}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400">
                            <option value="">All Flats</option>
                            {flats.map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                        <input type="date" value={filters.from_date} onChange={(e) => handleFilterChange('from_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                        <input type="date" value={filters.to_date} onChange={(e) => handleFilterChange('to_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">B2B / B2C</label>
                        <select value={filters.b2b_b2c} onChange={(e) => handleFilterChange('b2b_b2c', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            {B2B_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input type="text" placeholder="Bill no or member..." value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button onClick={handleReset} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">
                        <Icon icon="mdi:refresh" className="tw-text-sm" /> Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {COLUMNS.map(col => (
                                    <th key={col.key} className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                                        style={{ textAlign: col.align || 'left' }}>
                                        {col.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gstReportLoading ? (
                                <tr><td colSpan={COLUMNS.length} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                </td></tr>
                            ) : invoices.length === 0 ? (
                                <tr><td colSpan={COLUMNS.length} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No GST data found</p>
                                </td></tr>
                            ) : (
                                <>
                                    {invoices.map((inv) => {
                                        const hasDiff = (inv.gst_difference || 0) !== 0;
                                        return (
                                            <tr key={inv.invoice_id} className={`tw-border-b tw-border-gray-100 ${hasDiff ? 'tw-bg-red-50' : 'hover:tw-bg-gray-50/50'}`}>
                                                {COLUMNS.map(col => (
                                                    <td key={col.key}
                                                        className={`tw-px-3 tw-py-2 tw-whitespace-nowrap ${col.key === 'gst_difference' && hasDiff ? 'tw-text-red-700 tw-font-bold' : 'tw-text-gray-700'}`}
                                                        style={{ textAlign: col.align || 'left' }}>
                                                        {getCellValue(inv, col)}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                    {/* Summary Footer Row */}
                                    <tr className="tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 tw-font-bold">
                                        {COLUMNS.map((col, i) => {
                                            const summaryKey = SUMMARY_MAP[col.key];
                                            if (i === 0) return <td key={col.key} className="tw-px-3 tw-py-2.5 tw-text-gray-800">Totals</td>;
                                            if (!summaryKey) return <td key={col.key} className="tw-px-3 tw-py-2.5"></td>;
                                            const val = summary[summaryKey] || 0;
                                            return (
                                                <td key={col.key}
                                                    className={`tw-px-3 tw-py-2.5 tw-whitespace-nowrap tw-text-right ${col.key === 'gst_difference' && val !== 0 ? 'tw-text-red-700' : 'tw-text-gray-800'}`}>
                                                    {formatCurrency(val)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default GSTReport;
